import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Table from '../../components/table'
import Pagination from '../../components/pagination'
import UniversitiesFilter from './filter'
import ImageModal from '../../components/image-modal'
import { LogoCellRenderer, BadgeCellRenderer } from '../../components/table-cell-renderers'
import useTableDataSync from '../../infrastructure/event-bus/hooks/use-table-data-sync'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import EventBus from '../../infrastructure/event-bus/event-bus'
import general from '../../utils/general'
import RoleBasedAccess from '../../components/role-based-access'
import { UserTypeEnum } from '../../utils/enums'
import useI18n from '../../i18n/useI18n'
import { ApplicationForm, ApplicationOptionModal } from './components'

const Universities = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t, language } = useI18n()
  const userInfo = useSelector((state) => state.auth?.userInfo || {})
  const [universities, setUniversities] = useState([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1
  })

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    city_id: '',
    province: '',
    university_type: '',
    is_active: ''
  })

  // Image Modal states
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: '',
    imageAlt: ''
  })

  // Application Modal states
  const [isApplicationFormOpen, setIsApplicationFormOpen] = useState(false)
  const [isApplicationOptionModalOpen, setIsApplicationOptionModalOpen] = useState(false)
  const [selectedUniversityForApp, setSelectedUniversityForApp] = useState(null)

  const fetchUniversities = (page = 1, filterParams = {}, limit = null, sortParams = null) => {
    setLoading(true)

    // Build query parameters
    const queryParams = {
      page: page,
      limit: limit || pagination.limit
    }

    // Add filters if they have values
    if (filterParams.search && filterParams.search.trim()) {
      queryParams.search = filterParams.search.trim()
    }
    if (filterParams.city_id) {
      queryParams.city_id = filterParams.city_id
    }
    if (filterParams.province && filterParams.province.trim()) {
      queryParams.province = filterParams.province.trim()
    }
    if (filterParams.university_type && filterParams.university_type.trim()) {
      queryParams.university_type = filterParams.university_type
    }
    if (filterParams.is_active !== '' && filterParams.is_active !== null) {
      queryParams.is_active = filterParams.is_active
    }

    // Add sort parameters if provided
    if (sortParams && sortParams.sort && sortParams.order) {
      queryParams.sort = sortParams.sort
      queryParams.order = sortParams.order
    }

    dispatch(
      ApiCallActions.Get({
        url: 'universities',
        query: queryParams,
        onSuccess: ({ data }) => {
          setUniversities(data.data || [])
          setPagination({
            total: data.meta?.total || 0,
            page: data.meta?.page || 1,
            limit: data.meta?.limit || 10,
            totalPages: data.meta?.totalPages || 1
          })
          setLoading(false)
        },
        onError: () => {
          setUniversities([])
          setLoading(false)
        }
      })
    )
  }

  useEffect(() => {
    fetchUniversities(1, filters)
  }, [])

  const handlePageChange = (page) => {
    fetchUniversities(page, filters)
  }

  const handleLimitChange = (newLimit) => {
    const updatedPagination = {
      ...pagination,
      limit: newLimit,
      page: 1 // Reset to first page when limit changes
    }
    // Update pagination state immediately
    setPagination(updatedPagination)
    // Fetch with new limit
    fetchUniversities(1, filters, newLimit)
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
  }

  const handleFilterSubmit = (newFilters) => {
    setFilters(newFilters)
    fetchUniversities(1, newFilters)
  }

  const handleClearFilters = (clearedFilters) => {
    setFilters(clearedFilters)
    fetchUniversities(1, clearedFilters)
  }

  const handleSort = (columnKey, direction) => {
    const sortParams = direction ? { sort: columnKey, order: direction } : null
    fetchUniversities(pagination.page, filters, pagination.limit, sortParams)
  }

  const handleLogoClick = (imageUrl, imageAlt) => {
    if (imageUrl) {
      setImageModal({
        isOpen: true,
        imageUrl: imageUrl,
        imageAlt: imageAlt
      })
    }
  }

  const handleCloseModal = () => {
    setImageModal({
      isOpen: false,
      imageUrl: '',
      imageAlt: ''
    })
  }

  const handleApplyClick = (university) => {
    setSelectedUniversityForApp(university)
    const uniType = university?.universityType || university?.university_type

    if (uniType === 'public') {
      setIsApplicationOptionModalOpen(true)
    } else {
      setIsApplicationFormOpen(true)
    }
  }

  const handleApplicationFormClose = () => {
    setIsApplicationFormOpen(false)
    setSelectedUniversityForApp(null)
  }

  const handleApplicationOptionModalClose = () => {
    setIsApplicationOptionModalOpen(false)
    // Keep selectedUniversity for options
  }

  const handleApplicationOptionSelect = (option) => {
    if (option === 'agency') {
      setIsApplicationFormOpen(true)
    }
  }

  const getUserData = () => {
    if (!userInfo) return null
    return {
      firstName: userInfo.firstName || '',
      lastName: userInfo.lastName || '',
      email: userInfo.email || '',
      phone: userInfo.phone || '',
      userType: userInfo.userType || '',
      id: userInfo.id || '',
      agentId: userInfo.agentId || userInfo.agent?.id || '',
      fatherName: userInfo.fatherName || '',
      motherName: userInfo.motherName || '',
      phoneCountryCode: userInfo.phoneCountryCode || '+90',
      citizenship: userInfo.citizenship || '',
      residenceCountry: userInfo.residenceCountry || '',
      citizenshipStatus: userInfo.citizenshipStatus || ''
    }
  }

  const tableColumns = [
    {
      key: 'logoUrl',
      label: t('UNIVERSITIES_TABLE_COLUMN_LOGO'),
      sortable: false,
      render: (value, row) => (
        <LogoCellRenderer
          imageUrl={value}
          imageAlt={row.name || t('UNIVERSITIES_TABLE_COLUMN_LOGO')}
          onClick={handleLogoClick}
        />
      )
    },
    {
      key: 'name',
      label: t('UNIVERSITIES_TABLE_COLUMN_NAME'),
      sortable: true,
      render: (value, row) => general.getLocaleValue(row, 'name', language)
    },
    {
      key: 'country',
      label: t('UNIVERSITIES_TABLE_COLUMN_COUNTRY'),
      sortable: true
    },
    {
      key: 'city',
      label: t('UNIVERSITIES_TABLE_COLUMN_CITY'),
      sortable: true,
      render: (value, row) => row?.city?.cityName || value || t('COMMON_NOT_AVAILABLE')
    },
    {
      key: 'university_type',
      label: t('UNIVERSITIES_TABLE_COLUMN_TYPE'),
      sortable: true,
      render: (value, row) => {
        const uniTypeValue = value || row.universityType // fallback for camelCase responses
        const isPublic = uniTypeValue === 'public'
        const label = isPublic
          ? t('UNIVERSITIES_TYPE_PUBLIC')
          : uniTypeValue === 'private'
            ? t('UNIVERSITIES_TYPE_PRIVATE')
            : t('COMMON_NOT_AVAILABLE')
        const cssClass = uniTypeValue ? (isPublic ? 'badge-info' : 'badge-primary') : 'badge-default'
        return <span className={`badge ${cssClass}`}>{label}</span>
      }
    },
    {
      key: 'isActive',
      label: t('UNIVERSITIES_TABLE_COLUMN_STATUS'),
      sortable: true,
      render: (value) => (
        <BadgeCellRenderer
          value={value}
          options={{
            trueLabel: t('UNIVERSITIES_STATUS_ACTIVE'),
            falseLabel: t('UNIVERSITIES_STATUS_INACTIVE'),
            trueClass: 'badge-success',
            falseClass: 'badge-danger'
          }}
        />
      )
    }
  ]

  const handleEditClick = (row) => {
    navigate(`/universities/${row.id}/edit`)
  }

  const handleDeleteClick = async (row) => {
    const confirmed = await general.confirmDelete(t('UNIVERSITIES_ENTITY'), row.name)

    if (!confirmed) {
      return
    }

    dispatch(
      ApiCallActions.Delete({
        url: `universities/${row.id}`,
        showAlertOnError: true,
        showLoading: true,
        onSuccess: () => {
          // Emit event for entity deletion
          EventBus.sc.onEntityDeleted({
            entityType: 'university',
            entityId: row.id
          })

          // Show success message
          general.showDeleteSuccess(t('UNIVERSITIES_ENTITY'))
        }
      })
    )
  }

  // Auto-sync table data with entity events (create, update, delete)
  useTableDataSync(
    'university',
    universities,
    setUniversities,
    pagination,
    setPagination,
    (page, filterParams) => fetchUniversities(page, filterParams || filters),
    filters
  )

  const renderActions = (row) => {
    return (
      <div>
        <button
          className="btn btn-sm btn-info m-r-5 btn-icon btn-round" //daha az border radius için
          onClick={() => navigate(`/universities/${row.id}`)}
          title={t('UNIVERSITIES_ACTIONS_DETAIL')}
        >
          <i className="zmdi zmdi-eye"></i>
        </button>
        <button
          className="btn btn-sm btn-success m-r-5 btn-icon btn-round"
          onClick={() => handleApplyClick(row)}
          title={t('UNIVERSITIES_ACTIONS_APPLY', 'Hemen Başvur')}
        >
          <i className="zmdi zmdi-mail-send"></i>
        </button>
        <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
          <button
            className="btn btn-sm btn-warning m-r-5 btn-icon btn-round"
            onClick={() => handleEditClick(row)}
            title={t('UNIVERSITIES_ACTIONS_EDIT')}
          >
            <i className="zmdi zmdi-edit"></i>
          </button>
          <button
            className="btn btn-sm btn-danger btn-icon btn-round" //daha az border radius için  
            onClick={() => handleDeleteClick(row)}
            title={t('UNIVERSITIES_ACTIONS_DELETE')}
          >
            <i className="zmdi zmdi-delete"></i>
          </button>
        </RoleBasedAccess>
      </div>
    )
  }

  return (
    <section className="content">
      <div className="block-header">
        <div className="row">
          <div className="col-lg-7 col-md-6 col-sm-12 text-left" >
            <h2>{t('UNIVERSITIES_LIST_TITLE')}
              <small>{t('UNIVERSITIES_LIST_SUBTITLE')}</small>
            </h2>
          </div>
          <div className="col-lg-5 col-md-6 col-sm-12">
            <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
              <button
                className="btn btn-white btn-icon btn-round float-right m-l-10"
                type="button"
                onClick={() => navigate('/universities/new')}
              >
                <i className="zmdi zmdi-plus" title={t('UNIVERSITIES_ACTIONS_CREATE')}></i>
              </button>
            </RoleBasedAccess>
            <ul className="breadcrumb float-md-right">
              <li className="breadcrumb-item">
                <a href="/dashboard">
                  <i className="zmdi zmdi-home" style={{ marginRight: '5px' }}></i> {t('COMMON_HOME')}
                </a>
              </li>
              <li className="breadcrumb-item active">{t('UNIVERSITIES_BREADCRUMB_LIST')}</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container-fluid">
        <div className="row clearfix">
          <div className="col-lg-12">
            {/* Filter Card */}
            <UniversitiesFilter
              filters={filters}
              onFilterChange={handleFilterChange}
              onFilterSubmit={handleFilterSubmit}
              onClearFilters={handleClearFilters}
              onRefresh={() => fetchUniversities(pagination.page, filters, pagination.limit)}
              loading={loading}
            />

            {/* Table Card */}
            <Table
              columns={tableColumns}
              data={universities}
              loading={loading}
              emptyMessage={t('UNIVERSITIES_TABLE_EMPTY')}
              renderActions={renderActions}
              onSort={handleSort}
              pagination={pagination}
            />

            {/* Pagination Card */}
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          </div>
        </div>
      </div>

      {/* Image Modal */}
      <ImageModal
        isOpen={imageModal.isOpen}
        imageUrl={imageModal.imageUrl}
        imageAlt={imageModal.imageAlt}
        onClose={handleCloseModal}
      />

      {/* Application Option Modal - For Public Universities */}
      <ApplicationOptionModal
        isOpen={isApplicationOptionModalOpen}
        onClose={handleApplicationOptionModalClose}
        onSelectOption={handleApplicationOptionSelect}
        universityWebsite={selectedUniversityForApp?.websiteUrl || selectedUniversityForApp?.website_url}
        universityName={selectedUniversityForApp?.name || ''}
      />

      {/* Application Form Modal */}
      <ApplicationForm
        isOpen={isApplicationFormOpen}
        onClose={handleApplicationFormClose}
        program={null}
        universityName={selectedUniversityForApp?.name || ''}
        userData={getUserData()}
        universityId={selectedUniversityForApp?.id || ''}
      />
    </section>
  )
}

export default Universities